import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { BoardElement, Frame, MediaDto } from '@praesto/shared'
import { useText } from '../i18n.js'
import { CameraStore } from '../canvas/CameraStore.js'
import { BoardLayer } from '../canvas/BoardLayer.js'
import type { RemotePresence } from '../collab/usePresence.js'

interface Props {
  viewers: RemotePresence[]
  elements: BoardElement[]
  frames: Frame[]
  mediaById: Map<string, MediaDto>
  background: string
}

/**
 * Spiegelung dessen, was ein Gast gerade sieht.
 *
 * Kein Screensharing: das ist eine zweite Ansicht desselben Yjs-Dokuments mit
 * der Kamera des Gastes. Weil jeder Teilnehmer sein tatsächliches
 * Viewport-Rechteck in die Awareness zurückschreibt, kennt der Präsentierende
 * den echten Ausschnitt inklusive der beim Gast abgeschnittenen oder zusätzlich
 * sichtbaren Ränder.
 */
export function PreviewPanel({
  viewers,
  elements,
  frames,
  mediaById,
  background,
}: Props): React.ReactElement | null {
  const t = useText()
  const [index, setIndex] = useState(0)
  const camera = useMemo(() => new CameraStore(), [])
  const screenRef = useRef<HTMLDivElement>(null)
  const layerRef = useRef<HTMLDivElement>(null)

  const viewer = viewers[Math.min(index, Math.max(0, viewers.length - 1))]
  const viewport = viewer?.viewport ?? null

  useLayoutEffect(() => {
    const screen = screenRef.current
    if (!screen) return
    camera.attachLayer(layerRef.current)
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return
      camera.setSize({ width: entry.contentRect.width, height: entry.contentRect.height })
    })
    observer.observe(screen)
    camera.setSize({ width: screen.clientWidth, height: screen.clientHeight })
    return () => {
      observer.disconnect()
      camera.attachLayer(null)
    }
  }, [camera, viewers.length])

  useEffect(() => {
    if (!viewport) return
    // Ohne Zuschlag: die Vorschau soll exakt den Ausschnitt des Gastes zeigen.
    // Die Meldungen des Gastes kommen gedrosselt (rund zehnmal pro Sekunde),
    // deshalb wird die Lücke dazwischen überbrückt statt sprunghaft gesetzt.
    camera.adoptViewport(viewport, 1, 130)
  }, [camera, viewport])

  if (viewers.length === 0) {
    return (
      <aside className="preview">
        <header className="preview-head">
          <span>{t('preview.title')}</span>
        </header>
        <p className="preview-empty">{t('preview.empty')}</p>
      </aside>
    )
  }

  return (
    <aside className="preview">
      <header className="preview-head">
        <span className="preview-who">
          <span className="preview-dot" style={{ backgroundColor: viewer?.user.color }} />
          {viewer?.user.name}
          {viewer?.user.isGuest ? ` (${t('board.guest')})` : ''}
        </span>
        {viewers.length > 1 && (
          <span className="preview-switch">
            <button
              className="button ghost icon"
              onClick={() => setIndex((i) => (i - 1 + viewers.length) % viewers.length)}
              aria-label={t('preview.previous')}
            >
              ‹
            </button>
            <span className="preview-count">
              {Math.min(index, viewers.length - 1) + 1}/{viewers.length}
            </span>
            <button
              className="button ghost icon"
              onClick={() => setIndex((i) => (i + 1) % viewers.length)}
              aria-label={t('preview.next')}
            >
              ›
            </button>
          </span>
        )}
      </header>

      {/* Das Seitenverhältnis kommt vom Gast — sonst zeigte die Vorschau einen
          Ausschnitt, den so niemand vor sich hat. */}
      <div
        ref={screenRef}
        className="preview-screen"
        style={{
          aspectRatio: viewport ? `${viewport.w} / ${viewport.h}` : '16 / 10',
          backgroundColor: background,
        }}
      >
        <BoardLayer
          ref={layerRef}
          camera={camera}
          elements={elements}
          frames={frames}
          mediaById={mediaById}
          selection={null}
          canEdit={false}
        />
      </div>
    </aside>
  )
}
